import { useState } from "react";
import Joi from "joi-browser";

import useFormState from "../../../useFormState";
import {
  toAttributeRows,
  toAttributesObject,
  validateAttributeRows,
  PersonaAttributesValidationT,
} from "./personaAttributes";

export default useEvalRunConfigPersonaForm;

export { evalRunConfigPersonaFormSchema };

const evalRunConfigPersonaFormSchema = {
  name: Joi.string().required().label("Name"),
  description: Joi.string().required().label("Description"),
  scenario: Joi.string().required().label("Scenario"),
  initialMessage: Joi.string().required().label("Initial Message"),
  // Optional — a persona without one is still judged, just on the criteria alone.
  expectedOutput: Joi.string().allow("", null).label("Expected Output"),
  interruptionRate: Joi.number().min(0).max(1).label("Interruption Rate"),
  // Edited as rows; the rows are checked by validateAttributeRows, which can
  // report per-row and duplicate-key errors that Joi's messages can't.
  attributes: Joi.array().label("Attributes"),
};

const NO_ATTRIBUTE_ERRORS: PersonaAttributesValidationT = {
  isValid: true,
  rowErrors: [],
  error: undefined,
};

const getInitialPersona = (persona) => {
  return {
    name: persona.name ?? "",
    description: persona.description ?? "",
    scenario: persona.scenario ?? "",
    initialMessage: persona.initialMessage ?? "",
    expectedOutput: persona.expectedOutput ?? "",
    interruptionRate: persona.interruptionRate ?? 0,
    // Without this, saving the form dropped attributes a persona already had
    // (e.g. set through the API), since the saved persona is rebuilt from here.
    attributes: toAttributeRows(persona.attributes),
  };
};

function useEvalRunConfigPersonaForm(persona) {
  // Replaced: the attribute rows need their own validation and conversion back
  // to an object before the persona is saved.
  // const { formState, validate, errors, updateFormState } = useFormState(
  //   () => getInitialPersona(persona),
  //   evalRunConfigPersonaFormSchema,
  // );
  const {
    formState,
    validate: validateFields,
    errors,
    updateFormState: updateFormField,
  } = useFormState(
    () => getInitialPersona(persona),
    evalRunConfigPersonaFormSchema,
  );

  const [attributeErrors, setAttributeErrors] = useState(NO_ATTRIBUTE_ERRORS);

  const validate = () => {
    const areFieldsValid = validateFields();
    const attributesValidation = validateAttributeRows(formState.attributes);
    setAttributeErrors(attributesValidation);
    return areFieldsValid && attributesValidation.isValid;
  };

  const updateFormState = (field, value) => {
    updateFormField(field, value);
    if (field === "attributes") {
      setAttributeErrors(NO_ATTRIBUTE_ERRORS);
    }
  };

  // The persona as it is saved: attributes back to a key -> value object.
  const getPersona = () => ({
    ...formState,
    attributes: toAttributesObject(formState.attributes),
  });

  return {
    formState,
    validate,
    errors,
    attributeErrors,
    updateFormState,
    getPersona,
  };
}
